import { ClientEndpoint, ClientEndpoints, ClientEndpointType, Devices, DrakewellClientEndpoint, EmailClientEndpoint, EndpointStatus, Facility, FabricClientEndpoint, JsonClientEndpoint, LuxcomClientEndpoint, MS2ClientEndpoint, NtcipDmsClientEndpoint, TestEndpointCommand, WebRelayClientEndpoint } from "@app/shared";
import { useState } from "react";
import { useConfirmDialog, useFetch, useEditModelDialog } from "@app/shared/src/hooks";
import { CardPosition, Dialog, IconLink, Icons, PageContainer, SkeletonGrid, SortableTable, StatusDot, Table } from "@app/dashboard/src/components/shared";
import { DropDownMenu } from "@app/dashboard/src/components/shared/DropDownMenu";
import { ClientEndpointEditor } from "./EditClientEndpoint/ClientEndpointEditor";
import { EditEndpointTestCommand, TestSupportedEndpointTypes } from "@app/dashboard/src/components/pages/facility/EditEndpointTestCommand";
import { toastError } from "@app/dashboard/src/helpers/toastHelpers";
import { formatInTimeZone } from "date-fns-tz";
import { HistoryContentDialog, HistoryContent, parseHeaders, formatHeaderLines } from "./HistoryContentDialog";
import { HoverTooltip } from "@app/dashboard/src/components/shared/HoverTooltip";
import { useHref } from "react-router-dom";
import { apiDocsHrefForEndpoint } from "./EndpointApiDocs/specRegistry";
import { deliveryHealth, DeliveryHealth } from "./endpointHealth";
import { EndpointHealthStrip, LastFailureHover } from "./EndpointHealthStrip";
import { clientEndpointTypeLabel, clientEndpointTypeOptions } from "./endpointTypeLabels";
import { DrakewellTestFooter } from "./EditClientEndpoint/EditDrakewellClientEndpoint";
import { get } from "@app/shared/src/api/api_client";
import { compareVersions, DRAKEWELL_ENDPOINT_FIRMWARE_VERSION } from "@app/dashboard/src/helpers/versionCompare";

// Device-local delivery health for a Drakewell endpoint (webconfig only — served by the firmware's
// /api/clientendpoint/drakewell-status route; the cloud dashboard has no device to ask).
type DrakewellEndpointStatus = {
    endpointId: string;
    inUse?: boolean;
    bufferedEventCount: number;
    oldestBufferedUtc?: string;
    expectedIntervalSeconds?: number;
    lastSuccessUtc?: string;
    lastFailureUtc?: string;
    failingSinceUtc?: string;
    lastFailureMessage?: string;
    consecutiveFailures: number;
    parkedWindows: number;
};

// One-line delivery health. The verdict comes from the shared deliveryHealth() predicate so the cloud and
// the device agree; this component only renders it. Times are shown in the
// facility timezone with the zone abbreviation spelled out — a bare clock time is ambiguous for anyone
// not sitting in the device timezone. Shared by both hosts so device-local and cloud health render
// identically.
type EndpointHealthLineProps = {
    lastSuccessUtc?: string | Date;
    failingSinceUtc?: string | Date;
    lastFailureMessage?: string;
    consecutiveFailures: number;
    bufferedEventCount?: number;
    parkedWindows?: number;
      health: DeliveryHealth;
    zone: string;
};

// Formats a timestamp in the facility's zone, and never renders an object.
const zonedTime = (value: string | number | Date | undefined | null, zone: string): string => {
    if (value === null || value === undefined || value === "") return "no date";
    const d = value instanceof Date
        ? value
        : (typeof value === "string" || typeof value === "number") ? new Date(value) : undefined;
    if (d === undefined || isNaN(d.getTime())) return "no date";
    try {
        return formatInTimeZone(d, zone, "MM-dd p zzz");
    } catch {
        // Bad/unknown IANA zone — still show the instant rather than nothing.
        return d.toISOString();
    }
};

const EndpointHealthLine: React.FC<EndpointHealthLineProps> = (p) => {
    const at = (d?: string | Date) => zonedTime(d, p.zone);
    const { severity, isFailing, isBehind, behindSinceUtc } = p.health;
    const degraded = severity === "warn";
    const unknown = severity === "unknown";
    const color = severity === "error" ? ERROR_COLOR : degraded ? WARNING_COLOR : unknown ? MUTED_COLOR : SUCCESS_COLOR;
    const queued = p.bufferedEventCount ?? 0;
    return (
        <div className="text-xs mt-1 space-x-2">
            <span className={isFailing || degraded ? "font-semibold" : undefined} style={{ color }}>
                {isFailing
                    ? `failing (${p.consecutiveFailures}× since ${at(p.failingSinceUtc)})`
                    : p.lastSuccessUtc
                        // The reason is the point: "degraded" with no stated cause reads as a false alarm.
                        ? `${degraded ? `degraded — ${p.health.reason ?? "see history"}` : "healthy"} — last sent ${at(p.lastSuccessUtc)}`
                        : "no sends yet"}
            </span>
            {/* Queued events are ordinary throughput, so they read neutral. Only an un-draining buffer —
                oldest event older than several scheduled sends — is a warning, and it says so explicitly. */}
            {queued > 0 && (isBehind
                ? <span style={{ color: WARNING_COLOR }} className="font-semibold">behind — {queued} event(s) queued since {at(behindSinceUtc)}</span>
                : <span style={{ color: MUTED_COLOR }}>{queued} event(s) queued for next send</span>)}
            {(p.parkedWindows ?? 0) > 0 && <span style={{ color: ERROR_COLOR }}>{p.parkedWindows} window(s) dropped</span>}
            {isFailing && p.lastFailureMessage && <span className="text-gray-500 break-all">{p.lastFailureMessage}</span>}
        </div>
    );
};

// Endpoints nothing references show a quiet "not in use" instead of a (stale) health state.
const NotInUse: React.FC = () => <div className="text-xs mt-1 text-gray-500">not in use — no trigger sends to this endpoint</div>;

// A trigger sends here, this endpoint type does record deliveries, and none have happened yet. Shown
// only when reportsDeliveryHistory is true: saying it about a type that records nothing would read as
// "this endpoint is broken" rather than "nothing has been sent", which is worse than saying nothing.
const NoDeliveriesYet: React.FC = () => <div className="text-xs mt-1 text-gray-500">no deliveries yet</div>;

// Device-local health. The device keeps no delivery-history rollup, so it supplies no historySeverity and
// the verdict rests on live state (failure streak, buffer age) — which is exactly why this must go through
// deliveryHealth() rather than leaving severity unset and defaulting to green.
const DrakewellHealth: React.FC<{ status?: DrakewellEndpointStatus; zone: string }> = ({ status, zone }) =>
    status?.inUse === false ? (
        <NotInUse />
    ) : status ? (
        <EndpointHealthLine
            lastSuccessUtc={status.lastSuccessUtc}
            failingSinceUtc={status.failingSinceUtc}
            lastFailureMessage={status.lastFailureMessage}
            consecutiveFailures={status.consecutiveFailures}
            bufferedEventCount={status.bufferedEventCount}
            parkedWindows={status.parkedWindows}
            health={deliveryHealth({
                lastSuccessUtc: status.lastSuccessUtc,
                consecutiveFailures: status.consecutiveFailures,
                parkedWindows: status.parkedWindows,
                bufferedEventCount: status.bufferedEventCount,
                oldestBufferedUtc: status.oldestBufferedUtc,
                expectedIntervalSeconds: status.expectedIntervalSeconds,
            })}
            zone={zone}
        />
    ) : null;

// Accessible variants rather than the --*-icon toast colours: everything these are applied to here is
// 12px text on white, where `orange` measures 1.97:1 and `red` 4.0:1 against a 4.5:1 AA threshold.
// Defined in BOTH hosts' variables.css, since this component renders in the webconfig too.
const SUCCESS_COLOR = "var(--success-text)";
const ERROR_COLOR = "var(--error-text)";
const WARNING_COLOR = "var(--warning-text)";
// The var behind Tailwind's muted.body-text (see dashboard/tailwind.config.js) — not --muted-body-text,
// which doesn't exist and would silently resolve to inherit.
const MUTED_COLOR = "var(--text-body-muted)";

// Colour follows the recorded outcome, not the status code: a transport without one (email, web relay)
// would otherwise always render amber even when the delivery succeeded. The code still refines a
// failure — a 4xx the receiver will never accept reads differently from a timeout worth retrying.
const outcomeColor = (succeeded: boolean | undefined, code?: number): string => {
    if (succeeded) return SUCCESS_COLOR;
    if (code && code >= 400 && code < 500 && code !== 408 && code !== 429) return ERROR_COLOR;
    return WARNING_COLOR;
};

// One "Headers:"/"Body:" row in the history Response cell: a fixed-width label and a compact link that
// opens the full, formatted content in a dialog (rather than crowding the cell). Headers show their
// count; a body shows a "View" link. Hovering previews the content inline; empty values render a
// quiet dash.
const HistoryDetailRow: React.FC<{
    label: string;
    kind: "headers" | "body";
    value?: string | null;
    onOpen: (detail: HistoryContent) => void;
}> = ({ label, kind, value, onOpen }) => {
    const headerCount = kind === "headers" && value ? Object.keys(parseHeaders(value)).length : 0;
    // Headers preview as readable "Name: value" lines; a body previews as-is.
    const preview = kind === "headers" && value ? formatHeaderLines(value) : value;
    return (
        <div className="flex items-baseline gap-2 text-sm">
            <span className="text-muted-body-text w-14 shrink-0">{label}:</span>
            {value ? (
                <HoverTooltip
                    placement="top-start"
                    hoverable={
                        // A plain link, not a button — strip the browser's button border/background.
                        <span
                            className="text-link-page hover:text-link-page-hover underline cursor-pointer"
                            onClick={() => onOpen({ title: label, kind, content: value })}
                        >
                            {kind === "headers" ? `${headerCount} header${headerCount === 1 ? "" : "s"}` : "View"}
                        </span>
                    }
                >
                    <pre className="text-xs text-body-text max-w-md max-h-64 overflow-auto whitespace-pre-wrap break-words m-0">
                        {preview}
                    </pre>
                </HoverTooltip>
            ) : (
                <span className="text-muted-body-text">—</span>
            )}
        </div>
    );
};

// The editor children spread their typed sub-models into plain object literals before
// calling props.onChange, which loses the class identity. class-validator only finds
// decorators via the prototype chain, so the dialog's validateModel() saw zero errors
// (e.g. @IsUrl on JsonClientEndpoint.endpointUrl never fired) and Save went through.
// Rehydrate the correct subclass so validation actually gates Save.
const instantiateEndpoint = (m: any): ClientEndpoint => {
    switch (m?.endpointType) {
        case ClientEndpointType.Json: return new JsonClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.Email: return new EmailClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.Luxcom: return new LuxcomClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.NTCIP_DMS: return new NtcipDmsClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.WebRelay: return new WebRelayClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.MS2: return new MS2ClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.Drakewell: return new DrakewellClientEndpoint(m) as unknown as ClientEndpoint;
        case ClientEndpointType.Fabric: return new FabricClientEndpoint(m) as unknown as ClientEndpoint;
        default: return new ClientEndpoint(m);
    }
};

type ClientEndpointTableProps = {
    organizationId: string;
    facility?: Facility;
    facilityId?: string;
    facilityName?: string;
    // Facility IANA timezone for rendering history times, when the full facility object isn't passed
    // (the webconfig passes only ids). Falls back to facility.localTimezoneId, then the browser zone.
    localTimezoneId?: string;
    onDataChange?: () => void;
    offlineMode?: boolean;
    // Dashboard origin to send API-docs links to, for hosts that don't bundle the docs page (WebConfig).
    // Supplied by the device from `/settings.dashboardUrl` so it follows the device's environment.
    docsOrigin?: string;
};

export const ClientEndpointTable: React.FC<ClientEndpointTableProps> = (props) => {
    const [endpoints, , endpointHelpers] = useFetch(() => ClientEndpoints.getWithStatusByFacilityId(props.organizationId, props.facility?.id));
    const [selectedEndpoint, setSelectedEndpoint] = useState<ClientEndpoint>();
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [historyDetail, setHistoryDetail] = useState<HistoryContent | null>(null);
    const closeHistoryDialog = () => {
        setShowHistoryDialog(false);
        setSelectedEndpoint(undefined);
    };
    const [history, , historyHelpers] = useFetch(() => (showHistoryDialog && selectedEndpoint ? ClientEndpoints.getHistory(selectedEndpoint.id) : Promise.resolve(undefined)), [selectedEndpoint, showHistoryDialog]);
    const [showTestDialog, setShowTestDialog] = useState(false);
    const endpointDialog = useEditModelDialog(ClientEndpoint, false);
    const [endpointTestCommand, setEndpointTestCommand] = useState<TestEndpointCommand>();
    const confirmDialog = useConfirmDialog();
    // Basename-aware root so a per-endpoint docs link works behind the device proxy. Resolved once here
    // rather than per row because useHref is a hook and renderFunc runs inside the table's row loop.
    // WebConfig (offlineMode) has no local docs route, so it must supply docsOrigin — the dashboard it was
    // told to link to. Without it the helper returns undefined and the link is simply not rendered, rather
    // than pointing at a route that would 404. See EndpointApiDocs/paths.ts.
    const appBaseHref = useHref("/");
    const docsHrefFor = (endpoint: ClientEndpoint | undefined) =>
        apiDocsHrefForEndpoint(endpoint, {
            docsOrigin: props.docsOrigin,
            hostsDocsPage: !props.offlineMode,
            appBaseHref,
        });
    // Device-local Drakewell feed health (webconfig only; the endpoint lives on the device).
    const [drakewellStatuses] = useFetch(() =>
        props.offlineMode ? get<DrakewellEndpointStatus[]>("/api/clientendpoint/drakewell-status") : Promise.resolve(undefined)
    );

    // Dashboard only: the devices this endpoint scope filters down to (facility page → that facility's
    // devices; org settings page → every device in the org). Drakewell endpoints can only be CREATED
    // once all of them run firmware that understands the type — the server strips unsupported types
    // from device payloads (so old firmware never breaks), but a feed only some devices contribute to
    // would look like missing traffic to the partner, so creation is gated on the whole tree.
    const [scopeDevices, scopeDevicesError] = useFetch(() =>
        props.offlineMode
            ? Promise.resolve(undefined)
            : props.facilityId
                ? Devices.getByFacilityId(props.facilityId)
                : Devices.getByOrganizationId(props.organizationId),
        [props.offlineMode, props.facilityId, props.organizationId]
    );
    // CoreApi strips types a device's firmware can't parse, so offering one for an out-of-date device
    // would create an endpoint that silently never delivers.
    const firmwareDisabledReason = (minimumVersion: string) => {
        // Webconfig ships with the firmware: any type it can render is supported on that device.
        if (props.offlineMode) return undefined;
        // Fetch failed — fail open; the server-side gate still protects old devices.
        if (scopeDevicesError) return undefined;
        if (scopeDevices === undefined) return "Checking device firmware versions…";
        const outdated = scopeDevices.filter((d) => compareVersions(d.firmwareVersion ?? "0.0.0.0", minimumVersion) < 0);
        if (outdated.length === 0) return undefined;
        const scopeName = props.facilityId ? "location" : "organization";
        const names = outdated.slice(0, 3).map((d) => d.name || d.id).join(", ");
        return `Requires firmware ${minimumVersion} or newer on every device in this ${scopeName}. `
            + `${outdated.length} of ${scopeDevices.length} device(s) still need upgrading: ${names}${outdated.length > 3 ? ", …" : ""}`;
    };
    // Only types with no cloud path are gated here. Drakewell delivers from the device, so firmware
    // below the minimum means it can never deliver at all. A type the cloud can deliver is not gated:
    // firmware constrains only the device path, which is chosen per trigger via ProcessOnDevice, so
    // the warning for that belongs on the trigger (see EditEventTrigger).
    const disabledEndpointTypes = (() => {
        const gated: [ClientEndpointType, string][] = [
            [ClientEndpointType.Drakewell, DRAKEWELL_ENDPOINT_FIRMWARE_VERSION],
        ];
        const reasons: Partial<Record<ClientEndpointType, string>> = {};
        for (const [type, minimum] of gated) {
            const reason = firmwareDisabledReason(minimum);
            if (reason) reasons[type] = reason;
        }
        return Object.keys(reasons).length > 0 ? reasons : undefined;
    })();

    // || (not ??): an empty-string localTimezoneId must also fall back to the browser zone.
    const zone = props.facility?.localTimezoneId || props.localTimezoneId || Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).resolvedOptions().timeZone;

    const handleAddEndpointOnClick = () => {
        endpointDialog.show(
            async (toSave: Partial<ClientEndpoint>) => {
                await ClientEndpoints.update(toSave as ClientEndpoint);
                await endpointHelpers.refreshData();
                props.onDataChange?.();
            },
            "Add Endpoint",
            { organizationId: props.organizationId, facilityId: props.facilityId, endpointType: undefined } as Partial<ClientEndpoint>
        );
    };

    const handleEditEndpointOnClick = (toEdit: ClientEndpoint | undefined) => {
        if (!toEdit) return;

        endpointDialog.show(
            async (toSave: Partial<ClientEndpoint>) => {
                var result = await ClientEndpoints.update(toSave as ClientEndpoint);
                if (result) {
                    await endpointHelpers.refreshData();
                    props.onDataChange?.();
                }
            },
            "Edit Endpoint",
            toEdit
        );
        // show() wraps defaults in new ClientEndpoint() (the base class) so the dialog's
        // initial model has no subclass decorators. Replace it with the right subclass so
        // Save-time validation works even if the user never edits a subclass-only field.
        endpointDialog.modelHelpers.replaceModel(instantiateEndpoint(toEdit));
    };

    const handleSendTestOnClick = (toTest: ClientEndpoint | undefined) => {
        if (!toTest) return;
        setEndpointTestCommand(new TestEndpointCommand());
        setSelectedEndpoint(toTest);
        setShowTestDialog(true);
    };
    const sendTestToSelectedEndpoint = async () => {

        if (!selectedEndpoint || !endpointTestCommand) return;

        try {
            await ClientEndpoints.sendTestCommand(endpointTestCommand);
            setShowTestDialog(false);
        } catch (e) {
            toastError("Error Sending Test Command", "An error occurred while sending the test command (" + e + ")");
        }
    }

    const handleHistoryEndpointOnClick = (toView: ClientEndpoint | undefined) => {
        if (!toView) return;

        setSelectedEndpoint(toView);
        //Show history for this endpoint
        setShowHistoryDialog(true);
    };

    const handleDeleteEndpointOnClick = (toDelete: ClientEndpoint | undefined) => {
        if (!toDelete) return;
        confirmDialog.show("Delete Endpoint?", "Are you sure you want to delete this endpoint?", "Delete", async () => {
            await ClientEndpoints.deleteEndpoint(toDelete.id);
            await endpointHelpers.refreshData();
            props.onDataChange?.();
        });
    };

    const getStatus = (status: EndpointStatus | undefined) => {
        switch (status) {
            case EndpointStatus.Error:
                return "error";
            case EndpointStatus.Healthy:
                return "ok";
            case EndpointStatus.Warning:
                return "warn";
            default:
                return undefined;
        }
    };

    const statusFilterOptions = [
        { value: EndpointStatus.Unknown.toString(), label: "Unknown" },
        { value: EndpointStatus.Error.toString(), label: "Error" },
        { value: EndpointStatus.Warning.toString(), label: "Warning" },
        { value: EndpointStatus.Healthy.toString(), label: "Healthy" },
    ];

    return (
        <>
            <PageContainer
                Icon={Icons.Api}
                title="Client Endpoints"
                titleRight={
                    <div className="flex justify-center items-center">
                        <IconLink Icon={Icons.PlusCircle} onClick={handleAddEndpointOnClick}>
                            Add New
                        </IconLink>
                    </div>
                }
            >
                <SortableTable
                    initialSortKey="endpointName"
                    isLoading={endpointHelpers.isLoading}
                    emptyValue={<p className="m-5">No Client Endpoints Found</p>}
                    columns={[
                        {
                            header: "Status",
                            width: 50,
                            dataKey: "status",
                            filterable: true,
                            filterType: "select",
                            filterOptions: statusFilterOptions,
                            cardPosition: CardPosition.TopRight,
                            renderFunc: (e) => (
                                <div className="flex items-center h-full">
                                    <StatusDot status={getStatus(e?.status)} sizeClasses="w-3 h-3" />
                                </div>
                            ),
                        },
                        {
                            header: "Name",
                            width: 200,
                            dataKey: "endpointName",
                            filterable: true,
                            filterType: "text",
                        },
                        {
                            header: "Type",
                            width: 120,
                            dataKey: "endpointType",
                            filterable: true,
                            filterType: "select",
                            filterOptions: clientEndpointTypeOptions(),
                            // Sort by the visible label, not the enum value — "Closure (WebRelay)" is 5 and
                            // "Drakewell" is 7, so numeric order looks random to anyone reading names.
                            sortFunc: (a, b) => clientEndpointTypeLabel(a?.endpointType).localeCompare(clientEndpointTypeLabel(b?.endpointType)),
                            renderFunc: (e) => clientEndpointTypeLabel(e?.endpointType),
                        },
                        ...(!props.offlineMode ? [{
                            header: "Location",
                            width: 100,
                            dataKey: "facilityName" as const,
                            filterable: true,
                            filterType: "text" as const,
                            renderFunc: (e: ClientEndpoint | undefined) => (e?.facilityId == null ? "" : (e.facilityId == props.facilityId && props.facilityName?.length) ? props.facilityName : e.facilityName),
                        }] : []),
                        {
                            header: "Details",
                            width: 400,
                            dataKey: "description",
                            filterType: "text",
                            renderFunc: (e) => (
                                <>
                                    {e?.description}
                                    {props.offlineMode
                                        ? e?.endpointType === ClientEndpointType.Drakewell && (
                                            <DrakewellHealth status={drakewellStatuses?.find((s) => s.endpointId === e.id)} zone={zone} />
                                        )
                                        : null}
                                    {!props.offlineMode && e && (e.inUse === false
                                        ? <NotInUse />
                                        : !(e.lastSuccessUtc || e.failingSinceUtc)
                                        ? (e.reportsDeliveryHistory ? <NoDeliveriesYet /> : null)
                                        : (
                                            <LastFailureHover health={e.health} zone={zone}>
                                            <EndpointHealthLine
                                                lastSuccessUtc={e.lastSuccessUtc}
                                                failingSinceUtc={e.failingSinceUtc}
                                                lastFailureMessage={e.lastFailureMessage}
                                                consecutiveFailures={e.consecutiveFailures ?? 0}
                                                // The cloud's verdict comes from persisted delivery history; it has no
                                                // device-local buffer state to contribute.
                                                health={deliveryHealth({
                                                    lastSuccessUtc: e.lastSuccessUtc,
                                                    consecutiveFailures: e.consecutiveFailures ?? 0,
                                                    historySeverity: getStatus(e.status) ?? "unknown",
                                                    recentSendsConsidered: e.recentSendsConsidered,
                                                    recentFailureCount: e.recentFailureCount,
                                                    recentSlowSendCount: e.recentSlowSendCount,
                                                    recentMaxMs: e.recentMaxMs,
                                                })}
                                                zone={zone}
                                            />
                                            </LastFailureHover>
                                        ))}
                                    {/* History beside the live verdict: the line above says whether it is
                                        delivering now, this says how reliably it has been. Cloud only —
                                        e.health is never populated in offline mode. */}
                                    {!props.offlineMode && e?.inUse !== false && (
                                        <EndpointHealthStrip health={e?.health} zone={zone} />
                                    )}
                                </>
                            ),
                        },
                        {
                            header: "Docs",
                            width: 80,
                            renderFunc: (e) => {
                                const href = docsHrefFor(e);
                                return href ? (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-link-page underline whitespace-nowrap inline-flex items-center gap-1"
                                        title="Push API documentation — payload schema, auth, delivery semantics, PDF export"
                                        onClick={(ev) => ev.stopPropagation()}
                                    >
                                        <Icons.Info size={12} /> API Docs
                                    </a>
                                ) : null;
                            },
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (e) => (
                                <DropDownMenu>
                                    <IconLink onClick={() => handleHistoryEndpointOnClick(e)} Icon={Icons.Edit}>
                                        View History
                                    </IconLink>
                                    {e?.$type && TestSupportedEndpointTypes.includes(e.$type) && (
                                        <IconLink onClick={() => handleSendTestOnClick(e)} Icon={Icons.Play}>
                                            Send Test
                                        </IconLink>
                                    )}
                                    {/* No docs entry here — the Docs column already links every row that has
                                        one, and duplicating it in the menu just gave the same destination
                                        two controls. */}
                                    <IconLink onClick={() => handleEditEndpointOnClick(e)} Icon={Icons.Edit}>
                                        Edit
                                    </IconLink>
                                    <IconLink onClick={() => handleDeleteEndpointOnClick(e)} Icon={Icons.Trash}>
                                        Delete
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={endpoints}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {endpointDialog.renderDialog(
                (model, helpers) => model && <ClientEndpointEditor endpoint={model as ClientEndpoint} onChange={(newModel) => newModel && helpers.replaceModel(instantiateEndpoint(newModel))} offlineMode={props.offlineMode} docsOrigin={props.docsOrigin} disabledTypes={disabledEndpointTypes} />,
                undefined,
                (model) => (props.offlineMode && (model as ClientEndpoint)?.endpointType === ClientEndpointType.Drakewell)
                    ? <DrakewellTestFooter endpoint={model as DrakewellClientEndpoint} />
                    : <></>
            )}

            <Dialog
                show={showTestDialog}
                title={"Send Test"}
                onOkClick={async () => await sendTestToSelectedEndpoint()}
                okButtonText="Send"
                showCancelButton={true}
                onCancelClick={() => setShowTestDialog(false)}
            >
                {selectedEndpoint && endpointTestCommand && props.facility && <EditEndpointTestCommand devices={props.facility?.devices} endpoint={selectedEndpoint} value={endpointTestCommand} onChange={(newCommand) => setEndpointTestCommand(newCommand)} />}
            </Dialog>

            <Dialog
                show={showHistoryDialog}
                title={"Recent History"}
                onOkClick={closeHistoryDialog}
                onCancelClick={closeHistoryDialog}
                showCancelButton={false}
            >
                <Table.Container>
                    <Table.Header>
                        <Table.HeaderRow>
                            <Table.HeaderCol className="text-start">Date</Table.HeaderCol>
                            <Table.HeaderCol className="text-start">Status</Table.HeaderCol>
                            <Table.HeaderCol className="text-start">Response Time</Table.HeaderCol>
                            <Table.HeaderCol className="text-start">Response</Table.HeaderCol>
                        </Table.HeaderRow>
                    </Table.Header>
                    {historyHelpers.isLoading && <SkeletonGrid cols={3} rows={10} />}
                    {/* Rows must be wrapped: a <tr> directly inside <table> is invalid, and the browser
                        silently inserts a <tbody> of its own, which React then warns about. */}
                    <Table.Body>
                    {!historyHelpers.isLoading && !history?.length && (
                        <Table.Row>
                            <Table.Col className="text-center" colSpan={3}>
                                No history was found.
                            </Table.Col>
                        </Table.Row>
                    )}
                    {history?.map((h) => (
                        <Table.Row key={h.id}>
                            <Table.Col>{h?.dateCreated ? zonedTime(h.dateCreated, zone) : "no date"}</Table.Col>
                            <Table.Col>
                                <span className="font-semibold" style={{ color: outcomeColor(h.succeeded, h.responseCode) }}>
                                    {/* Transports without a status code report the outcome in words instead. */}
                                    {h.responseCode ?? (h.succeeded ? "sent" : "failed")}
                                </span>
                            </Table.Col>
                            <Table.Col>{(h.millisecondsToRespond ?? 0) / 1000}s</Table.Col>
                            <Table.Col>
                                <div className="space-y-1">
                                    {/* A send that never got a response has no code, headers or body — its
                                        exception message is the only account of what went wrong, so it leads
                                        the cell rather than hiding behind a "View" link. */}
                                    {h.exceptionMessage && (
                                        <div className="flex items-baseline gap-2 text-sm">
                                            <span className="text-muted-body-text w-14 shrink-0">Error:</span>
                                            <span className="break-words" style={{ color: ERROR_COLOR }}>{h.exceptionMessage}</span>
                                        </div>
                                    )}
                                    <HistoryDetailRow label="Headers" kind="headers" value={h.responseHeaders} onOpen={setHistoryDetail} />
                                    <HistoryDetailRow label="Body" kind="body" value={h.response} onOpen={setHistoryDetail} />
                                </div>
                            </Table.Col>
                        </Table.Row>
                    ))}
                    </Table.Body>
                </Table.Container>
            </Dialog>

            <HistoryContentDialog detail={historyDetail} onClose={() => setHistoryDetail(null)} />
        </>
    );
};
